import { useEffect, useRef, useState, useCallback } from 'react';

export function useAlertSound() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const alertPlayingRef = useRef(false);                     // Track if alert is sounding

  // --- Initialize audio element ---
  useEffect(() => {
    try {
      audioRef.current = new Audio('/sounds/alert.mp3');
      audioRef.current.preload = 'auto';
      audioRef.current.volume = 0.7;
    } catch (error) {
      console.warn('Audio initialization failed:', error);
      audioRef.current = null;
    }

    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.src = '';
        } catch (e) {}
        audioRef.current = null;
      }
    };
  }, []);

  // --- Stop alert (external use) ---
  const stopAlert = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (e) {}
      alertPlayingRef.current = false;   // mark as stopped
    }
  }, []);

  // --- Silently unlock audio context on first user gesture ---
  const unlockAudio = useCallback(async () => {
    if (isUnlocked) return;

    try {
      const tempAudio = new Audio();     // temporary element, does NOT touch main alert
      tempAudio.volume = 0;
      await tempAudio.play();
      tempAudio.pause();
      tempAudio.remove();
      setIsUnlocked(true);
      console.log('Audio context unlocked silently');
    } catch (err) {
      console.warn('Audio unlock failed:', err);
    }
  }, [isUnlocked]);

  // One-time global listener to unlock on first interaction
  useEffect(() => {
    const events = ['click', 'touchstart', 'keydown'];
    const handler = () => unlockAudio();
    events.forEach(e => document.addEventListener(e, handler, { once: true }));
    return () => events.forEach(e => document.removeEventListener(e, handler));
  }, [unlockAudio]);

  // --- Play alert (looping handled by the caller if needed, or we can add loop later) ---
  const playAlert = useCallback(() => {
    if (!isSoundEnabled || !audioRef.current) return;

    const audio = audioRef.current;
    audio.currentTime = 0;
    audio.loop = true;                   // assume alert loops; adjust if not

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          alertPlayingRef.current = true; // mark as playing after successful play
        })
        .catch(err => {
          console.warn('Audio play failed:', err);
          if (err.name === 'NotAllowedError') {
            unlockAudio().then(() => {
              audio.currentTime = 0;
              audio.play().catch(e => console.warn('Retry failed:', e));
            });
          }
        });
    }

    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
  }, [isSoundEnabled, unlockAudio]);

  // --- Toggle sound enabled ---
  const toggleSound = useCallback(() => {
    setIsSoundEnabled(prev => !prev);
  }, []);

  // --- Mute on ANY user interaction while alert is playing ---
    useEffect(() => {
      const muteOnInteraction = () => {
        if (
          alertPlayingRef.current &&
          audioRef.current &&
          !audioRef.current.paused &&
          isSoundEnabled          // only mute if sound is currently enabled
        ) {
          // Immediately stop playback
          stopAlert();
          // Disable sound so future playAlert() calls are ignored
          setIsSoundEnabled(false);
        }
      };

      const events = ['click', 'touchstart', 'scroll', 'keydown'] as const;
      events.forEach(evt =>
        document.addEventListener(evt, muteOnInteraction, { passive: true })
      );

      return () =>
        events.forEach(evt =>
          document.removeEventListener(evt, muteOnInteraction)
        );
    }, [stopAlert, isSoundEnabled]);   // added isSoundEnabled to dependencies

  // Ensure alertPlayingRef resets if audio ends naturally (loop=false case)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const endedHandler = () => {
      alertPlayingRef.current = false;
    };
    audio.addEventListener('ended', endedHandler);
    return () => audio.removeEventListener('ended', endedHandler);
  }, []);

  return {
    stopAlert,
    playAlert,
    isSoundEnabled,
    toggleSound,
  };
}
